/**
 * 資料ダウンロード／モックアップ相談／チェックリスト／お問い合わせ ページ Script
 *
 * - Pickles フォームの検証・送信ロジックを集約（テンプレ側はマークアップのみ）。
 *   フォーム固有の値（data-form-key）は DOM から読み取り、複数フォームに対応。
 *   設問が多いフォーム向けに、任意機能としてステップ制御（.pf-step）と
 *   入力途中の一時保存（data-pf-draft-key）を持つ。該当マークアップが無い
 *   フォームでは何も起きない。
 * - FONTPLUS をフォーム要素へ再適用。
 * - ロード時フェードイン（コンタクトと同じ gsap 設定）。EN見出し非表示のため
 *   JA見出しを pageTitleIntro の遅延制御から外し、l-section と同時にフェードさせる。
 *
 * @package Spice_Theme
 */

import { gsap } from 'gsap';

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const PF_ENDPOINT_BASE =
  'https://pickles.internal.spice-factory.co.jp/api/public/forms/';
// 送信成功後の遷移先（完了ページ）の既定値。全環境で効くよう相対パスで指定。
// フォームごとに `data-complete-url` を持たせると、そちらが優先される
// （Library は資料ごとの完了ページへ遷移させるため）。
const PF_COMPLETE_URL = '/contact/complete/';
// 入力途中の一時保存に使う localStorage キーの接頭辞。
// フォームごとの識別子は `data-pf-draft-key` で与える。
const PF_DRAFT_PREFIX = 'pickles_form_draft:';

// ---------------------------------------------------------------------------
// JA見出しを pageTitleIntro に拾わせない（footer スクリプトのためヘッダーはパース済み）。
// 初期 opacity:0 にして、l-section と同時にフェードインさせる。
// ---------------------------------------------------------------------------
const titleJa = document.querySelector('.p-document-dl .js-page-title-ja');
if (titleJa) {
  titleJa.classList.remove('js-page-title-ja');
  gsap.set(titleJa, { opacity: REDUCED ? 1 : 0 });
}

// ---------------------------------------------------------------------------
// FONTPLUS 再適用（フォーム要素／入力文字に Web フォントを当てる）
// ---------------------------------------------------------------------------
function fpReload() {
  if (window.FONTPLUS && typeof window.FONTPLUS.reload === 'function') {
    window.FONTPLUS.reload();
  }
}

// ---------------------------------------------------------------------------
// GA4 フォーム計測
//
// GA4 の拡張計測機能「フォームの操作」は、FONTPLUS が Web フォント取得に使う
// 隠しフォーム（form[target^="fontplus_iframe_"]）の programmatic submit まで
// フォーム操作として数えてしまう。FONTPLUS は header.php で全ページに読み込まれる
// ため、フォームの無いページでも form_start / form_submit がほぼ毎ページビュー
// 発火し、GA4 のイベント指標が使い物にならなくなる。
// そのため同機能は GA4 管理画面で無効化し、フォーム計測は実フォーム（.pf-form）
// だけを対象とするこの実装へ一本化する。
// ---------------------------------------------------------------------------

/**
 * GA4 へフォームイベントを送る。計測の失敗がフォーム送信を妨げないよう、
 * gtag 未読込（本番以外）でも例外時でも黙って何もしない。
 *
 * @param {string} name GA4 イベント名（form_start / form_submit）
 * @param {HTMLFormElement} form 対象フォーム
 */
function pfSendGa4Event(name, form) {
  if (typeof window.gtag !== 'function') return;
  try {
    window.gtag('event', name, {
      // Pickles のフォームキーはテナント解決に使う公開識別子（個人情報を含まない）。
      form_id: form.dataset.formKey,
      form_destination: location.pathname,
    });
  } catch (e) {
    /* 計測は送信処理を止めない */
  }
}

// ---------------------------------------------------------------------------
// Pickles フォーム
// ---------------------------------------------------------------------------
function pfUuid() {
  try {
    return crypto.randomUUID();
  } catch (e) {
    return 'pf-' + Date.now() + '-' + Math.random().toString(36).slice(2);
  }
}

function pfTracking() {
  const p = new URLSearchParams(location.search);
  const t = {
    current_url: location.href,
    current_path: location.pathname,
    referrer: document.referrer || '',
  };
  [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'wbraid',
    'gbraid',
    'fbclid',
  ].forEach(k => {
    const v = p.get(k);
    if (v) t[k] = v;
  });
  try {
    const KEY = 'pickles_first_touch';
    const ex = localStorage.getItem(KEY);
    if (ex) {
      const ft = JSON.parse(ex);
      t.first_landing_url = ft.first_landing_url;
      t.first_referrer = ft.first_referrer;
    } else {
      t.first_landing_url = t.current_url;
      t.first_referrer = t.referrer;
      localStorage.setItem(
        KEY,
        JSON.stringify({
          first_landing_url: t.current_url,
          first_referrer: t.referrer,
        })
      );
    }
  } catch (e) {
    /* localStorage 不可環境は無視 */
  }

  // GA4 client_id（_ga Cookie から受動的に取得。値の形式が合わない場合は付与しない）
  try {
    const gaMatch = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
    if (gaMatch) {
      const gaParts = decodeURIComponent(gaMatch[1]).split('.');
      if (gaParts.length === 4) {
        const gaClientId = gaParts[2] + '.' + gaParts[3];
        if (gaClientId.length <= 64 && /^\d+\.\d+$/.test(gaClientId)) {
          t.ga_client_id = gaClientId;
        }
      }
    }
  } catch (e) {
    /* Cookie 参照不可環境は無視 */
  }

  // クリックID（gclid/gbraid）の Cookie フォールバック。
  // URL に正しい値がある場合はそちらを優先し上書きしない。
  const clickIdPattern = /^[A-Za-z0-9_-]{1,256}$/;
  if (!t.gclid || !clickIdPattern.test(t.gclid)) {
    try {
      const awMatch = document.cookie.match(/(?:^|;\s*)_gcl_aw=([^;]+)/);
      if (awMatch) {
        const awParts = decodeURIComponent(awMatch[1]).split('.');
        if (
          awParts.length >= 3 &&
          awParts[0] === 'GCL' &&
          /^\d+$/.test(awParts[1])
        ) {
          const awId = awParts.slice(2).join('.');
          if (clickIdPattern.test(awId)) t.gclid = awId;
        }
      }
    } catch (e) {
      /* Cookie 参照不可環境は無視 */
    }
  }
  if (!t.gbraid || !clickIdPattern.test(t.gbraid)) {
    try {
      const gbMatch = document.cookie.match(/(?:^|;\s*)_gcl_gb=([^;]+)/);
      if (gbMatch) {
        const gbParts = decodeURIComponent(gbMatch[1]).split('.');
        if (
          gbParts.length >= 3 &&
          gbParts[0] === 'GCL' &&
          /^\d+$/.test(gbParts[1])
        ) {
          const gbId = gbParts.slice(2).join('.');
          if (clickIdPattern.test(gbId)) t.gbraid = gbId;
        }
      }
    } catch (e) {
      /* Cookie 参照不可環境は無視 */
    }
  }

  // Meta 広告向け素データ（値の検証のみ行い、そのまま保存する）
  try {
    // eslint-disable-next-line no-control-regex -- Meta fbc の形式検証: 制御文字を明示的に除外する必要がある
    const fbcPattern = /^fb\.\d+\.\d+\.[^\s\x00-\x1F\x7F]+$/;
    const fbcMatch = document.cookie.match(/(?:^|;\s*)_fbc=([^;]+)/);
    if (fbcMatch) {
      const fbcVal = decodeURIComponent(fbcMatch[1]);
      if (fbcVal.length <= 512 && fbcPattern.test(fbcVal)) t.fbc = fbcVal;
    }
  } catch (e) {
    /* Cookie 参照不可環境は無視 */
  }
  try {
    // eslint-disable-next-line no-control-regex -- Meta fbp の形式検証: 制御文字を明示的に除外する必要がある
    const fbpPattern = /^fb\.\d+\.\d+\.[^\s\x00-\x1F\x7F]+$/;
    const fbpMatch = document.cookie.match(/(?:^|;\s*)_fbp=([^;]+)/);
    if (fbpMatch) {
      const fbpVal = decodeURIComponent(fbpMatch[1]);
      if (fbpVal.length <= 512 && fbpPattern.test(fbpVal)) t.fbp = fbpVal;
    }
  } catch (e) {
    /* Cookie 参照不可環境は無視 */
  }

  return t;
}

/**
 * ラジオグループの選択値（未選択なら空文字）を返す。条件表示の判定に使う。
 * name は Pickles の項目名（英字始まりの英数字と _）に限られるため、
 * 属性セレクタへそのまま埋めて問題ない。
 *
 * @param {HTMLFormElement} form 対象フォーム
 * @param {string} name 項目名
 * @returns {string} 選択値
 */
function pfRadioValue(form, name) {
  const checked = form.querySelector(
    'input[type="radio"][name="' + name + '"]:checked'
  );
  return checked ? checked.value : '';
}

/**
 * 選択肢グループ（同名の複数コントロールで1つの設問を成す）に属するか。
 * ラジオは常に該当。チェックボックスは複数選択の設問（.pf-control を持つもの）だけが
 * 該当し、同意チェック（data-consent）は単独の設問なので該当しない。
 *
 * @param {HTMLInputElement} el 対象コントロール
 * @returns {boolean} 選択肢グループか
 */
function pfIsChoiceGroup(el) {
  return (
    el.type === 'radio' ||
    (el.type === 'checkbox' && el.classList.contains('pf-control'))
  );
}

/**
 * 選択肢グループの同名コントロールを返す。
 * name は Pickles の項目名（英字始まりの英数字と _）に限られるため、
 * 属性セレクタへそのまま埋めて問題ない。
 *
 * @param {HTMLFormElement} form 対象フォーム
 * @param {HTMLInputElement} el グループ内の任意のコントロール
 * @returns {NodeListOf<HTMLInputElement>} 同一グループのコントロール
 */
function pfChoiceGroup(form, el) {
  return form.querySelectorAll(
    'input[type="' + el.type + '"][name="' + el.name + '"]'
  );
}

/**
 * 選択肢グループに1つ以上選択があるか。
 *
 * @param {HTMLFormElement} form 対象フォーム
 * @param {HTMLInputElement} el グループ内の任意のコントロール
 * @returns {boolean} 選択があるか
 */
function pfHasChoice(form, el) {
  return !!form.querySelector(
    'input[type="' + el.type + '"][name="' + el.name + '"]:checked'
  );
}

function initPicklesForm(form) {
  const formKey = form.dataset.formKey;
  if (!formKey) return;
  const endpoint = PF_ENDPOINT_BASE + formKey + '/submissions';
  // 遷移先は同一オリジンのパスのみ受け付ける（外部サイトへの誘導を防ぐ）。
  const completeUrl = (() => {
    const raw = form.dataset.completeUrl;
    if (!raw) return PF_COMPLETE_URL;
    try {
      const parsed = new URL(raw, location.origin);
      return parsed.origin === location.origin
        ? parsed.pathname + parsed.search
        : PF_COMPLETE_URL;
    } catch (e) {
      return PF_COMPLETE_URL;
    }
  })();
  // 冪等キー: フォーム単位で1回だけ生成し、再送でも同じトークンを使う（重複登録・重複通知の防止）。
  const submissionToken = pfUuid();

  // aria-describedby は補足文（.pf-help）を指していることがある。エラーIDは
  // 置き換えではなく追記し、エラー解除時に元の値へ戻す。
  function describeWithError(input, errorId) {
    const current = input.getAttribute('aria-describedby') || '';
    input.dataset.pfDescribedby = current;
    input.setAttribute(
      'aria-describedby',
      current ? current + ' ' + errorId : errorId
    );
  }
  function restoreDescribedby(input) {
    if (!('pfDescribedby' in input.dataset)) return;
    const original = input.dataset.pfDescribedby;
    if (original) input.setAttribute('aria-describedby', original);
    else input.removeAttribute('aria-describedby');
    delete input.dataset.pfDescribedby;
  }

  function showError(input, msg) {
    const fieldEl = input.closest('.pf-field');
    if (!fieldEl) return;
    const p = document.createElement('p');
    p.className = 'pf-error';
    p.id = (input.id || 'pf-' + (input.name || 'field')) + '-error';
    p.setAttribute('role', 'alert');
    p.textContent = msg;
    fieldEl.appendChild(p);

    if (pfIsChoiceGroup(input)) {
      const choices = fieldEl.querySelector('.pf-choices');
      if (choices) choices.classList.add('is-error');
      pfChoiceGroup(form, input).forEach(el => {
        el.setAttribute('aria-invalid', 'true');
        describeWithError(el, p.id);
      });
      return;
    }

    const controlEl =
      input.type === 'checkbox' ? input.closest('.pf-check') : input;
    if (controlEl) controlEl.classList.add('is-error');
    input.setAttribute('aria-invalid', 'true');
    describeWithError(input, p.id);
  }

  function clearFieldError(input) {
    const field = input.closest('.pf-field');
    if (field) {
      const e = field.querySelector('.pf-error');
      if (e) e.remove();
      const choices = field.querySelector('.pf-choices');
      if (choices) choices.classList.remove('is-error');
    }
    const group = pfIsChoiceGroup(input) ? pfChoiceGroup(form, input) : [input];
    group.forEach(el => {
      el.classList.remove('is-error');
      el.removeAttribute('aria-invalid');
      restoreDescribedby(el);
    });
    const lbl = input.closest('.pf-check');
    if (lbl) lbl.classList.remove('is-error');
  }

  function fieldMessage(input) {
    if (pfIsChoiceGroup(input)) {
      return input.hasAttribute('required') && !pfHasChoice(form, input)
        ? 'いずれかを選択してください。'
        : '';
    }
    if (input.type === 'checkbox') {
      return input.hasAttribute('required') && !input.checked
        ? 'プライバシーポリシーへの同意が必要です。'
        : '';
    }
    if (input.hasAttribute('required') && !input.value.trim()) {
      return 'この必須項目を入力してください。';
    }
    if (
      input.type === 'email' &&
      input.value.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.value.trim())
    ) {
      return '正しいメールアドレスを入力してください。';
    }
    return '';
  }

  function validateField(input) {
    clearFieldError(input);
    const msg = fieldMessage(input);
    if (msg) {
      showError(input, msg);
      return false;
    }
    return true;
  }

  // 検証対象。選択肢グループ（ラジオ／複数選択チェックボックス）はグループの先頭だけを
  // 代表として扱い、1つの設問に選択肢の数だけエラーが出るのを防ぐ。
  function validationTargets() {
    const seenGroups = new Set();
    const targets = [];
    form.querySelectorAll('.pf-control, input[data-consent]').forEach(el => {
      if (pfIsChoiceGroup(el)) {
        const groupKey = el.type + ':' + el.name;
        if (seenGroups.has(groupKey)) return;
        seenGroups.add(groupKey);
      }
      targets.push(el);
    });
    return targets;
  }

  /**
   * 指定範囲を検証し、最初にエラーになったコントロールを返す（無ければ null）。
   *
   * @param {HTMLElement} scope 検証範囲（ステップ要素またはフォーム全体）
   * @returns {HTMLElement|null} 最初のエラー要素
   */
  function validateScope(scope) {
    let firstErr = null;
    validationTargets().forEach(input => {
      if (!scope.contains(input)) return;
      if (!validateField(input) && !firstErr) firstErr = input;
    });
    return firstErr;
  }

  // ---------------------------------------------------------------
  // 入力途中の一時保存
  //
  // 設問が多いフォームでは、リロードや誤操作での離脱が入力のやり直しに直結する。
  // data-pf-draft を持つステップ配下の項目だけを localStorage に保持し、
  // 個人情報を含むステップは対象外にしている。送信成功時に破棄する。
  // ---------------------------------------------------------------
  const draftKey = form.dataset.pfDraftKey
    ? PF_DRAFT_PREFIX + form.dataset.pfDraftKey
    : '';
  const draftInputs = draftKey
    ? Array.from(form.querySelectorAll('[data-pf-draft] .pf-control[name]'))
    : [];

  function restoreDraft() {
    if (!draftInputs.length) return;
    let saved;
    try {
      saved = JSON.parse(localStorage.getItem(draftKey) || 'null');
    } catch (e) {
      return;
    }
    if (!saved || typeof saved !== 'object') return;
    draftInputs.forEach(el => {
      const value = saved[el.name];
      if (el.type === 'checkbox') {
        if (Array.isArray(value)) el.checked = value.indexOf(el.value) >= 0;
        return;
      }
      if (typeof value !== 'string' || value === '') return;
      if (el.type === 'radio') {
        if (el.value === value) el.checked = true;
      } else {
        el.value = value;
      }
    });
  }
  function saveDraft() {
    if (!draftInputs.length) return;
    const snapshot = {};
    draftInputs.forEach(el => {
      if (el.type === 'radio') {
        if (el.checked) snapshot[el.name] = el.value;
      } else if (el.type === 'checkbox') {
        if (!Array.isArray(snapshot[el.name])) snapshot[el.name] = [];
        if (el.checked) snapshot[el.name].push(el.value);
      } else if (el.value) {
        snapshot[el.name] = el.value;
      }
    });
    try {
      localStorage.setItem(draftKey, JSON.stringify(snapshot));
    } catch (e) {
      /* localStorage 不可環境は無視 */
    }
  }
  function clearDraft() {
    if (!draftKey) return;
    try {
      localStorage.removeItem(draftKey);
    } catch (e) {
      /* localStorage 不可環境は無視 */
    }
  }

  // 条件表示の初期判定より前に復元する（復元値に応じて条件フィールドを開くため）。
  restoreDraft();

  if (draftInputs.length) {
    let draftTimer;
    const scheduleSave = () => {
      clearTimeout(draftTimer);
      draftTimer = setTimeout(saveDraft, 500);
    };
    form.addEventListener('input', scheduleSave);
    form.addEventListener('change', scheduleSave);
  }

  // blur で即検証 / 入力・変更でエラーが出ている項目を再検証。
  // 選択肢グループは blur 検証をしない（未選択のグループを Tab で通過しただけで
  // エラーが出ると、まだ答えていないだけの利用者を責めることになる）。
  form.querySelectorAll('.pf-control, input[data-consent]').forEach(el => {
    const isChoice = el.type === 'checkbox' || el.type === 'radio';
    if (!pfIsChoiceGroup(el)) {
      el.addEventListener('blur', () => validateField(el));
    }
    el.addEventListener(isChoice ? 'change' : 'input', () => {
      const field = el.closest('.pf-field');
      if (
        el.classList.contains('is-error') ||
        (field && field.querySelector('.pf-error'))
      ) {
        validateField(el);
      }
    });
  });

  // 条件表示フィールド: data-pf-show-when で指定した項目の値が
  // data-pf-show-value のときだけ表示する。表示中は必須になるが、
  // data-pf-optional を付けた項目は任意のままにする。
  // （例: 連絡方法が「お電話」のときだけ電話番号欄を出して必須にする）
  form.querySelectorAll('[data-pf-show-when]').forEach(condEl => {
    const controllerName = condEl.getAttribute('data-pf-show-when');
    const controls = form.querySelectorAll('[name="' + controllerName + '"]');
    if (!controls.length) return;
    const isRadioController = controls[0].type === 'radio';
    const showValue = condEl.getAttribute('data-pf-show-value');
    const keepOptional = condEl.hasAttribute('data-pf-optional');
    const inner = condEl.querySelector('.pf-control');
    const applyConditional = () => {
      const current = isRadioController
        ? pfRadioValue(form, controllerName)
        : controls[0].value;
      condEl.hidden = current !== showValue;
      if (!inner) return;
      if (!condEl.hidden) {
        if (!keepOptional) inner.setAttribute('required', '');
      } else {
        inner.removeAttribute('required');
        inner.value = '';
        clearFieldError(inner);
      }
    };
    applyConditional();
    controls.forEach(control =>
      control.addEventListener('change', applyConditional)
    );
  });

  // ---------------------------------------------------------------
  // ステップ制御（.pf-step が2つ以上あるフォームだけ有効）
  //
  // 設問数の体感を下げるのが目的。JS が無効／失敗した場合は全ステップが
  // そのまま縦に並ぶ（is-stepped が付かないため CSS の出し分けが効かない）。
  // ---------------------------------------------------------------
  const steps = Array.from(form.querySelectorAll('.pf-step'));
  const isStepped = steps.length > 1;
  const progress = form.querySelector('[data-pf-progress]');
  let currentStep = 0;

  function showStep(index, silent) {
    currentStep = index;
    steps.forEach((step, i) => step.classList.toggle('is-active', i === index));
    if (progress) {
      progress
        .querySelectorAll('[data-pf-progress-item]')
        .forEach((item, i) => {
          item.classList.toggle('is-current', i === index);
          item.classList.toggle('is-done', i < index);
          if (i === index) item.setAttribute('aria-current', 'step');
          else item.removeAttribute('aria-current');
        });
    }
    if (silent) return;
    // 画面が丸ごと入れ替わるため、見出しへフォーカスを移して読み上げ位置も合わせる。
    const title = steps[index].querySelector('.pf-step__title');
    if (title) title.focus({ preventScroll: true });
    const top = form.getBoundingClientRect().top + window.scrollY - 120;
    window.scrollTo({
      top: Math.max(top, 0),
      behavior: REDUCED ? 'auto' : 'smooth',
    });
  }

  function goNext() {
    const firstErr = validateScope(steps[currentStep]);
    if (firstErr) {
      firstErr.focus();
      return;
    }
    showStep(Math.min(currentStep + 1, steps.length - 1));
  }

  if (isStepped) {
    form.classList.add('is-stepped');
    if (progress) progress.hidden = false;
    showStep(0, true);

    form.querySelectorAll('[data-pf-next]').forEach(btn => {
      btn.addEventListener('click', goNext);
    });
    form.querySelectorAll('[data-pf-prev]').forEach(btn => {
      btn.addEventListener('click', () =>
        showStep(Math.max(currentStep - 1, 0))
      );
    });

    // 最終ステップ以外での Enter は送信ではなく「次へ」に割り当てる
    // （途中のステップで送信されると、まだ見ていない項目のエラーが並ぶ）。
    form.addEventListener('keydown', e => {
      if (e.key !== 'Enter' || e.target.tagName === 'TEXTAREA') return;
      if (currentStep >= steps.length - 1) return;
      e.preventDefault();
      goNext();
    });
  }

  // GA4: 実ユーザーが入力を始めた時点で1回だけ form_start を送る。
  let ga4Started = false;
  const sendFormStart = () => {
    if (ga4Started) return;
    ga4Started = true;
    pfSendGa4Event('form_start', form);
  };
  form.addEventListener('input', sendFormStart);
  form.addEventListener('change', sendFormStart);

  // 入力文字にも FONTPLUS を当てる（軽く間引き）
  let fpTimer;
  form.addEventListener('input', () => {
    clearTimeout(fpTimer);
    fpTimer = setTimeout(fpReload, 400);
  });

  form.addEventListener('submit', e => {
    e.preventDefault();
    const firstErr = validateScope(form);
    if (firstErr) {
      // エラーが今表示していないステップにある場合は、そのステップへ切り替えてから促す。
      if (isStepped) {
        const stepIndex = steps.indexOf(firstErr.closest('.pf-step'));
        if (stepIndex >= 0 && stepIndex !== currentStep) showStep(stepIndex);
      }
      firstErr.focus();
      return;
    }

    // GA4: 検証を通過した実際の送信だけを form_submit として数える。
    // 送信成功後は完了ページへ遷移するため、遷移と競合しないここで送る。
    pfSendGa4Event('form_submit', form);

    const btn = form.querySelector('[type="submit"]');
    if (btn) btn.disabled = true;

    // 送信対象は .pf-control を持つ名前付きコントロール（input/select/textarea）から動的に収集。
    // フォームごとに項目が異なっても対応（ハニーポット website・同意チェックは対象外）。
    // ⚠️ フォーム追加時の注意: 送信したい入力欄には必ず .pf-control を付けること。
    //    付け忘れると name があっても収集対象から静かに漏れる（payload に乗らない）。
    const data = {};
    form.querySelectorAll('.pf-control[name]').forEach(el => {
      // 非表示の条件フィールド（hidden な .pf-field 内）は送信対象から除外する。
      // ステップの出し分けは class で行うため、表示中でないステップの入力は残る。
      if (el.closest('[hidden]')) return;
      // ラジオは選択済みのものだけ（未選択の同名コントロールが後勝ちで空を書かないように）。
      if (el.type === 'radio' && !el.checked) return;
      // 複数選択（同名チェックボックス）は配列で送る。1つも選ばれていない場合も
      // 空配列を送り、項目自体は payload に残す。
      if (el.type === 'checkbox') {
        if (!Array.isArray(data[el.name])) data[el.name] = [];
        if (el.checked) data[el.name].push(el.value);
        return;
      }
      data[el.name] = el.value;
    });
    const hp = form.querySelector('[name="website"]');
    if (hp && hp.value) data.website = hp.value;

    const consent = {
      privacyPolicyAgreed: false,
      personalDataProcessingConsent: false,
      marketingEmailConsent: false,
    };
    form.querySelectorAll('input[data-consent]').forEach(el => {
      const w = el.getAttribute('data-consent');
      if (w === 'privacy') consent.privacyPolicyAgreed = el.checked;
      else if (w === 'personal')
        consent.personalDataProcessingConsent = el.checked;
      else if (w === 'marketing') consent.marketingEmailConsent = el.checked;
    });

    fetch(endpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        submissionToken,
        data,
        tracking: pfTracking(),
        consent,
      }),
    })
      .then(res => {
        if (res.ok) {
          clearDraft();
          window.location.href = completeUrl;
        } else {
          if (btn) btn.disabled = false;
          alert('送信に失敗しました。入力内容をご確認ください。');
        }
      })
      .catch(() => {
        if (btn) btn.disabled = false;
        alert('送信に失敗しました。時間をおいて再度お試しください。');
      });
  });
}

// ---------------------------------------------------------------------------
document.addEventListener('DOMContentLoaded', () => {
  // フォーム初期化（複数フォーム対応）
  document.querySelectorAll('.pf-form[data-form-key]').forEach(initPicklesForm);

  // ロード時フェード（見出し＋本文セクションを同時に）
  const section = document.querySelector('.p-document-dl .l-section');
  const fadeTargets = [section, titleJa].filter(Boolean);
  if (fadeTargets.length) {
    if (REDUCED) {
      gsap.set(fadeTargets, { opacity: 1 });
    } else {
      gsap.to(fadeTargets, {
        opacity: 1,
        ease: 'power3.out',
        duration: 0.8,
        delay: 0.2,
      });
    }
  }
});

// FONTPLUS 初回適用
if (document.readyState === 'complete') {
  fpReload();
} else {
  window.addEventListener('load', fpReload);
}
